import { API_BASE_URL } from '../config/api';
import type { AppDocument } from '../types/document';

/**
 * Reads the auth token from localStorage.
 * This matches your current frontend auth pattern.
 */
function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('account-token');

  return token
    ? {
        Authorization: `Bearer ${token}`,
      }
    : {};
}

// Reads an error body safely from a failed fetch response.
async function readErrorMessage(response: Response): Promise<string> {
  try {
    const data = await response.json();
    return data?.message || `Request failed with status ${response.status}`;
  } catch {
    return `Request failed with status ${response.status}`;
  }
}

/**
 * Fetches the document content as a Blob using authenticated fetch.
 *
 * Why:
 * We do not use window.open("/api/documents/:id/content") directly because
 * that would not include the Authorization header from localStorage.
 */
async function fetchDocumentBlob(documentId: string): Promise<Blob> {
  const response = await fetch(`${API_BASE_URL}/api/documents/${documentId}/content`, {
    method: 'GET',
    headers: {
      ...getAuthHeaders(),
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(await readErrorMessage(response));
  }

  return response.blob();
}

//Opens a Blob in a new browser tab.
function openBlobInNewTab(blob: Blob): void {
  const blobUrl = URL.createObjectURL(blob);
  window.open(blobUrl, '_blank', 'noopener,noreferrer');

  // Cleanup after a delay so the browser has time to load the Blob URL
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}

// Triggers a browser download for a Blob.
function downloadBlob(blob: Blob, filename: string): void {
  const blobUrl = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();

  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}

/**
 * Normalizes any existing saved document-like values into AppDocument[].
 *
 * This is useful while converting frames because some older saved data may use:
 * - name
 * - filename
 * - type
 * - mimeType
 * - uploadDate
 */
function normalizeOne(input: unknown): AppDocument | null {
  if (!input || !input.id) return null;

  return {
    id: String(input.id),
    name: input.name || input.filename || 'Document',
    mimeType: input.mimeType || input.type,
    size: typeof input.size === 'number' ? input.size : undefined,
    createdAt: input.createdAt || input.uploadDate,
  };
}

export const documentService = {
  // Upload a new document through the new backend route.
  async upload(file: File): Promise<AppDocument> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/api/documents`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
      },
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(await readErrorMessage(response));
    }

    const data = await response.json();

    if (!data?.success || !data?.document) {
      throw new Error('Invalid upload response');
    }

    return {
      id: data.document.id,
      name: data.document.name,
      mimeType: data.document.mimeType,
      size: data.document.size,
      createdAt: data.document.createdAt,
    };
  },

  //Open a document in a new tab by fetching it first as a Blob.
  async open(documentId: string): Promise<void> {
    const blob = await fetchDocumentBlob(documentId);
    openBlobInNewTab(blob);
  },

  //Download a document by fetching it first as a Blob.
  async download(documentId: string, filename: string): Promise<void> {
    const blob = await fetchDocumentBlob(documentId);
    downloadBlob(blob, filename);
  },

  // Permanently delete the document.
  // In your current product behavior, remove == delete.
  async delete(documentId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/documents/${documentId}`, {
      method: 'DELETE',
      headers: {
        ...getAuthHeaders(),
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(await readErrorMessage(response));
    }
  },

  // Normalize unknown saved values into a clean AppDocument[] array.
  normalizeMany(input: unknown): AppDocument[] {
    if (!Array.isArray(input)) return [];
    return input.map(normalizeOne).filter(Boolean) as AppDocument[];
  },
};